import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";

type LogLevel = "error" | "warn" | "info";

interface LogEntry {
  level: LogLevel;
  message: string;
  stack_trace?: string;
  url?: string;
  user_agent?: string;
  user_id?: string;
  metadata?: Record<string, unknown>;
}

class Logger {
  private buffer: LogEntry[] = [];
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private readonly FLUSH_INTERVAL_MS = 10_000;
  private readonly MAX_BUFFER_SIZE = 50;

  constructor() {
    this.flushInterval = setInterval(() => this.flush(), this.FLUSH_INTERVAL_MS);

    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => this.flush());
    }
  }

  /**
   * Redact tokens, emails, JWTs, API keys and long hex/base64 secrets from a string.
   * Applied to every free-form field before it is sent to app_logs.
   */
  private redact(input: string): string {
    if (!input) return input;
    return input
      // JWTs (three base64url segments)
      .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, "[redacted-jwt]")
      // Bearer / Authorization tokens
      .replace(/(bearer|authorization|api[-_]?key|token|password|secret)\s*[:=]\s*"?[^\s"&,;]+/gi, "$1=[redacted]")
      // Emails
      .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[redacted-email]")
      // Long hex/base64 strings (likely keys)
      .replace(/\b[A-Fa-f0-9]{32,}\b/g, "[redacted-hex]")
      .replace(/\bsb_(secret|publishable)_[A-Za-z0-9_-]+/g, "[redacted-sb-key]");
  }

  private redactUrl(url?: string): string | undefined {
    if (!url) return url;
    try {
      const u = new URL(url);
      // Strip query and hash — they can carry tokens/PII
      return `${u.origin}${u.pathname}`;
    } catch {
      return this.redact(url);
    }
  }

  private redactMetadata(meta?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!meta) return meta;
    const SENSITIVE_KEYS = /^(password|token|secret|api[-_]?key|authorization|access[-_]?token|refresh[-_]?token|jwt|cookie|session)$/i;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(meta)) {
      if (SENSITIVE_KEYS.test(k)) {
        out[k] = "[redacted]";
      } else if (typeof v === "string") {
        out[k] = this.redact(v);
      } else {
        out[k] = v;
      }
    }
    return out;
  }

  private createEntry(
    level: LogLevel,
    message: string,
    options?: { stack?: string; metadata?: Record<string, unknown> }
  ): LogEntry {
    return {
      level,
      message: this.redact(message).substring(0, 2000),
      stack_trace: options?.stack ? this.redact(options.stack).substring(0, 5000) : undefined,
      url: this.redactUrl(typeof window !== "undefined" ? window.location.href : undefined),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      metadata: this.redactMetadata(options?.metadata),
    };
  }

  error(message: string, options?: { stack?: string; metadata?: Record<string, unknown> }) {
    const entry = this.createEntry("error", message, options);
    console.error(`[Logger] ${message}`, options?.metadata);
    // Forward to Sentry
    if (options?.stack) {
      const err = new Error(message);
      err.stack = options.stack;
      Sentry.captureException(err, { extra: options?.metadata });
    } else {
      Sentry.captureMessage(message, { level: "error", extra: options?.metadata });
    }
    this.buffer.push(entry);
    // Flush immediately for errors
    this.flush();
  }

  warn(message: string, options?: { metadata?: Record<string, unknown> }) {
    const entry = this.createEntry("warn", message, options);
    console.warn(`[Logger] ${message}`, options?.metadata);
    Sentry.captureMessage(message, { level: "warning", extra: options?.metadata });
    this.buffer.push(entry);
    if (this.buffer.length >= this.MAX_BUFFER_SIZE) this.flush();
  }

  info(message: string, options?: { metadata?: Record<string, unknown> }) {
    const entry = this.createEntry("info", message, options);
    console.info(`[Logger] ${message}`, options?.metadata);
    this.buffer.push(entry);
    if (this.buffer.length >= this.MAX_BUFFER_SIZE) this.flush();
  }

  async flush() {
    if (this.buffer.length === 0) return;

    const entries = [...this.buffer];
    this.buffer = [];

    try {
      // Try to get user id
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      const rows = entries.map((e) => ({
        ...e,
        user_id: userId || null,
      }));

      const { error } = await supabase.from("app_logs").insert(rows);

      if (error) {
        console.error("[Logger] Failed to flush logs:", error.message);
        // Re-add failed entries (up to limit)
        this.buffer.push(...entries.slice(0, this.MAX_BUFFER_SIZE));
      }
    } catch (err: unknown) {
      console.error("[Logger] Flush exception:", err);
      this.buffer.push(...entries.slice(0, this.MAX_BUFFER_SIZE));
    }
  }

  destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    this.flush();
  }
}

export const logger = new Logger();
